import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { FormattedMessage } from '@zenbusiness/application-commons-intl';
import Text from '@zenbusiness/zui/Text';

import useBankingApplicationStatus from '#client/hooks/useBankingApplicationStatus';
import { useOrderTrackerStatus } from '#client/hooks/useOrderTrackerStatus';
import { useProductCards } from '#client/hooks/useProductCards';
import { ProductCards } from '#client/pages/Products/components/ProductCards';

import { HomeSection } from '../HomeSection';
import { WHILE_YOU_WAIT_TITLE_OVERRIDES } from './constants';
import {
  getTimelineItems, getUpsellItems, shouldFetchBankingStatus, shouldHideBankingTask
} from './utils';

function WhileYouWait() {
  const {
    entitlements,
    businessEntity: { domesticJurisdiction }
  } = useBusinessEntity();
  const { data: productCardsData, loading: productCardsLoading } = useProductCards();

  const { orderTasksWithStatus } = useOrderTrackerStatus();
  const allowedTimelineItems = getTimelineItems(orderTasksWithStatus);

  // Check if we have any pending banking tasks that need application status
  const hasPendingBankingTask = allowedTimelineItems.some(shouldFetchBankingStatus);
  const { applicationStatus: bankingApplicationStatus } = useBankingApplicationStatus(
    hasPendingBankingTask
  );

  // Filter out banking tasks with final statuses
  const visibleTimelineItems = allowedTimelineItems.filter(
    (item) => !shouldHideBankingTask(item, bankingApplicationStatus)
  );

  // Flatten upsells across all visible timeline items so we render every entry
  const upsells = visibleTimelineItems
    .flatMap((item) => getUpsellItems(item.type, entitlements, domesticJurisdiction))
    .filter((upsell) => !upsell.useSlideSheet && Boolean(upsell.url));

  const upsellServiceCodeToUrl = upsells.reduce((acc, upsell) => {
    const codes = [upsell.entitlement];
    codes.forEach((code, idx) => {
      const existing = acc[code];
      if (!existing || idx < existing.priority) {
        acc[code] = { url: upsell.url, priority: idx };
      }
    });
    return acc;
  }, {});
  const upsellServiceCodes = new Set(Object.keys(upsellServiceCodeToUrl));
  const upsellProductCards = (productCardsData?.cards || [])
    .filter(({ serviceCode }) => upsellServiceCodes.has(serviceCode))
    .map((card) => {
      const override = upsellServiceCodeToUrl[card.serviceCode];
      const titleOverride = WHILE_YOU_WAIT_TITLE_OVERRIDES[card.serviceCode];
      return {
        ...card,
        ...(override?.url && { primaryPath: override.url, primaryExternal: null }),
        ...(titleOverride && { titleKey: titleOverride })
      };
    })
    .slice(0, 3);

  if (!visibleTimelineItems.length) {
    return null;
  }

  if (!upsells.length) {
    return null;
  }

  // If product cards are still loading, show the spinner; if they finished and we
  // have no matching cards, skip rendering the section.
  if (!productCardsLoading && !upsellProductCards.length) {
    return null;
  }

  return (
    <HomeSection
      subtitle={(
        <Text color="secondary" size="md">
          <FormattedMessage id="dashboard.while_you_wait.subtitle" />
        </Text>
      )}
      title={(
        <Text color="primary" size="xl" weight="medium">
          <FormattedMessage id="dashboard.while_you_wait.title" />
        </Text>
      )}
    >
      <ProductCards
        columns={upsellProductCards.length}
        items={upsellProductCards}
        loading={productCardsLoading}
        elementPosition="order_tracker_recommendations"
      />
    </HomeSection>
  );
}

WhileYouWait.propTypes = {};

export default WhileYouWait;
